import { createI18n } from 'vue-i18n'
import type { SupportedLocales, I18nConfig } from './types/i18n'
import {
  detectLocale,
  LOCALE_INFO,
  setDocumentLanguage,
  setDocumentDirection,
} from './utils/detectLocale'

// Configuration
const config: I18nConfig = {
  defaultLocale: 'en',
  fallbackLocale: 'en',
  availableLocales: ['en', 'it', 'es'],
  detectBrowserLanguage: true,
  persistLocale: true,
  storageKey: 'app_locale',
}

// Import the full English locale as default
import en from './locales/en'

const messages = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  en: en as any,
}

// Create i18n instance
export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: config.fallbackLocale,
  messages,
  datetimeFormats: {
    en: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    it: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
    es: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
      },
    },
  },
})

// Load locale messages dynamically
export async function loadLocale(locale: SupportedLocales) {
  if (locale === 'en') return // Already loaded

  try {
    const module = await import(`./locales/${locale}/index.ts`)
    const localeMessages = module.default || module[locale]

    i18n.global.setLocaleMessage(locale, localeMessages)
  } catch (error) {
    console.error(`Failed to load locale ${locale}:`, error)
  }
}

// Single entry point for changing the locale. Messages are loaded BEFORE the locale is
// switched, so anything reacting to the switch (see the PrimeVue locale watcher in main.ts)
// resolves its translations in the new language rather than the English fallback
export async function setI18nLocale(locale: SupportedLocales) {
  await loadLocale(locale)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(i18n.global.locale as any).value = locale
  setDocumentLanguage(locale)
  setDocumentDirection(locale)
}

// Setup function for app initialization
export async function setupI18n() {
  await setI18nLocale(detectLocale())

  return i18n
}

// Preload specific locales
export async function preloadLocales(locales: SupportedLocales[]) {
  const promises = locales.map((locale) => loadLocale(locale))
  await Promise.allSettled(promises)
}

// Get available locales info
export function getAvailableLocalesInfo() {
  return config.availableLocales.map((code) => LOCALE_INFO[code])
}

// Check if locale is loaded
export function isLocaleLoaded(locale: SupportedLocales): boolean {
  return (i18n.global.availableLocales as string[]).includes(locale)
}

// Get current locale
export function getCurrentLocale(): SupportedLocales {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (i18n.global.locale as any).value as SupportedLocales
}

// Export configuration
export { config as i18nConfig }

export default i18n
